import { FileText, Info, Plus, Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { formatDate } from '@/lib/dateFormat';
import {
  mensagemDoErro,
  ROTULO_DO_PUBLICO,
  useComunicados,
  usePublicarComunicado,
  type ComunicadoOficial,
  type SituacaoDoComunicado,
} from '@/lib/useComunicados';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { EmptyState } from '@/ui/EmptyState';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { PageHeader } from '@/ui/PageHeader';
import { PaginacaoCompacta } from '@/ui/PaginacaoCompacta';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';

import { FormularioDeComunicado } from './FormularioDeComunicado';

const TAMANHO_DA_PAGINA = 20;

const SELO_DA_SITUACAO: Record<SituacaoDoComunicado, { status: StatusVariant; label: string }> = {
  RASCUNHO: { status: 'pendente', label: 'Rascunho' },
  PUBLICADO: { status: 'aprovado', label: 'Publicado' },
  EXPIRADO: { status: 'suspenso', label: 'Expirado' },
  CANCELADO: { status: 'rejeitado', label: 'Cancelado' },
};

function Vigencia({ c }: { c: ComunicadoOficial }) {
  return (
    <>
      Vigência: {formatDate(c.vigenciaInicio)}{' '}
      {c.vigenciaFim ? `→ ${formatDate(c.vigenciaFim)}` : '(sem fim)'}
    </>
  );
}

export default function ComunicadosList() {
  const [pagina, setPagina] = useState(1);
  const consulta = useComunicados({ pagina, tamanho: TAMANHO_DA_PAGINA });
  const publicar = usePublicarComunicado();
  const [formulario, setFormulario] = useState(false);
  // Uma montagem nova do formulário por abertura: nada sobra da vez anterior.
  const [aberturas, setAberturas] = useState(0);

  const comunicados = consulta.data?.itens ?? [];

  function abrirFormulario() {
    setAberturas((n) => n + 1);
    setFormulario(true);
  }

  function publicarComunicado(id: string) {
    publicar.mutate(id, {
      onSuccess: () => toast.success('Comunicado publicado.'),
      onError: (erro) =>
        toast.error(mensagemDoErro(erro, 'Não foi possível publicar o comunicado. Tente de novo.')),
    });
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Comunicados Oficiais"
        description="Avisos oficiais da DTP aos credenciados e operadores."
      >
        <Button onClick={abrirFormulario}>
          <Plus className="mr-2 h-4 w-4" aria-hidden />
          Novo comunicado
        </Button>
      </PageHeader>

      <Alert>
        <Info className="h-4 w-4" aria-hidden />
        <AlertDescription>
          Publicar registra o comunicado como oficial, com a data e o responsável, e ele não pode
          mais ser alterado. A exibição aos destinatários e a confirmação de leitura ainda não estão
          disponíveis.
        </AlertDescription>
      </Alert>

      <EstadoDaTela
        carregando={consulta.isPending}
        erro={consulta.error}
        aoTentarDeNovo={() => void consulta.refetch()}
        descricao="os comunicados"
        esqueleto="lista-de-cartoes"
      >
        <Card>
          <CardContent className="p-0">
            {comunicados.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="Nenhum comunicado"
                description="Crie o primeiro comunicado oficial."
              />
            ) : (
              <ul className="divide-y" aria-label="Comunicados oficiais">
                {comunicados.map((c) => (
                  <li
                    key={c.id}
                    className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate text-base font-medium">{c.titulo}</h2>
                        <StatusBadge {...SELO_DA_SITUACAO[c.status]} />
                        <span className="text-xs text-muted-foreground">
                          {ROTULO_DO_PUBLICO[c.publicoAlvo]}
                        </span>
                      </div>
                      {c.resumo && (
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                          {c.resumo}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        <Vigencia c={c} />
                        {c.publicadoEm && <> · Publicado em {formatDate(c.publicadoEm)}</>}
                      </p>
                    </div>
                    {c.status === 'RASCUNHO' && (
                      <ConfirmDialog
                        trigger={
                          <Button size="sm" disabled={publicar.isPending}>
                            <Send className="mr-2 h-4 w-4" aria-hidden />
                            Publicar
                          </Button>
                        }
                        title="Publicar este comunicado?"
                        description="Depois de publicado, o comunicado fica registrado como oficial e não pode mais ser alterado."
                        confirmLabel="Publicar"
                        onConfirm={() => publicarComunicado(c.id)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <PaginacaoCompacta
          pagina={pagina}
          tamanho={TAMANHO_DA_PAGINA}
          total={consulta.data?.total ?? 0}
          aoMudar={setPagina}
        />
      </EstadoDaTela>

      <FormularioDeComunicado key={aberturas} aberto={formulario} aoMudarAberto={setFormulario} />
    </div>
  );
}
